<template>
    <span ref="anchorRef" class="tooltip-anchor">
        <slot name="trigger" />

        <Teleport to="body">
            <Transition :css="false" @enter="enterTooltip" @leave="leaveTooltip">
                <div
                    v-if="isVisible"
                    :id="tooltipId"
                    ref="tooltipRef"
                    class="tooltip-content"
                    :class="{ 'tooltip-content--wrap': wrap }"
                    :style="tooltipStyle"
                    role="tooltip"
                >
                    {{ text }}
                </div>
            </Transition>
        </Teleport>
    </span>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right'

/**
 * Публичный API Tooltip:
 * - слот `trigger` — элемент, по hover или focus-within которого показывается подсказка;
 * - `text` — обязательный текст подсказки;
 * - `placement` — top | bottom | left | right (по умолчанию top);
 * - `delay` — задержка показа в миллисекундах (по умолчанию 0);
 * - `wrap` — разрешает перенос длинного текста.
 *
 * Для существующих вызовов без слота триггером остаётся родительский элемент,
 * поэтому новый API можно подключать без дублирования расчёта геометрии.
 */
interface Props {
    text?: string
    placement?: TooltipPlacement
    delay?: number
    wrap?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    delay: 0,
    wrap: false
})

const anchorRef = ref<HTMLElement | null>(null)
const tooltipRef = ref<HTMLElement | null>(null)
const isVisible = ref(false)
const isPointerInside = ref(false)
const isFocusInside = ref(false)
const tooltipStyle = ref<Record<string, string>>({})
const tooltipId = `tooltip-${Math.random().toString(36).slice(2, 10)}`
const placement = computed<TooltipPlacement>(() => props.placement || 'top')
const shouldBeVisible = computed(() => isPointerInside.value || isFocusInside.value)
let showTimer: ReturnType<typeof setTimeout> | null = null
let triggerElement: HTMLElement | null = null
let isPositionTracking = false

const clearShowTimer = () => {
    if (showTimer) {
        clearTimeout(showTimer)
        showTimer = null
    }
}

const positionTooltip = () => {
    const tooltip = tooltipRef.value
    if (!triggerElement || !tooltip) return

    const triggerRect = triggerElement.getBoundingClientRect()
    const tooltipWidth = tooltip.offsetWidth
    const tooltipHeight = tooltip.offsetHeight
    const gap = 12
    const edge = 8
    const maxLeft = Math.max(edge, window.innerWidth - tooltipWidth - edge)
    const maxTop = Math.max(edge, window.innerHeight - tooltipHeight - edge)
    let left = triggerRect.left + (triggerRect.width - tooltipWidth) / 2
    let top = triggerRect.top + (triggerRect.height - tooltipHeight) / 2

    if (placement.value === 'top') top = triggerRect.top - tooltipHeight - gap
    if (placement.value === 'bottom') top = triggerRect.bottom + gap
    if (placement.value === 'left') left = triggerRect.left - tooltipWidth - gap
    if (placement.value === 'right') {
        left = triggerRect.right + gap
        if (left + tooltipWidth > window.innerWidth - edge) {
            left = triggerRect.left - tooltipWidth - gap
        }
    }

    tooltipStyle.value = {
        left: `${Math.min(Math.max(left, edge), maxLeft)}px`,
        top: `${Math.min(Math.max(top, edge), maxTop)}px`
    }
}

const showTooltip = () => {
    if (!props.text || isVisible.value) return

    clearShowTimer()
    showTimer = setTimeout(async () => {
        showTimer = null
        if (!shouldBeVisible.value) return

        isVisible.value = true
        await nextTick()
        addAriaDescription()
        startPositionTracking()
        positionTooltip()
    }, props.delay)
}

const addAriaDescription = () => {
    if (!triggerElement) return

    const ids = (triggerElement.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter(Boolean)
    if (!ids.includes(tooltipId)) {
        triggerElement.setAttribute('aria-describedby', [...ids, tooltipId].join(' '))
    }
}

const removeAriaDescription = () => {
    if (!triggerElement) return

    const ids = (triggerElement.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter((id) => id && id !== tooltipId)
    if (ids.length) triggerElement.setAttribute('aria-describedby', ids.join(' '))
    else triggerElement.removeAttribute('aria-describedby')
}

const startPositionTracking = () => {
    if (isPositionTracking) return

    isPositionTracking = true
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
}

const stopPositionTracking = () => {
    if (!isPositionTracking) return

    isPositionTracking = false
    window.removeEventListener('resize', updatePosition)
    window.removeEventListener('scroll', updatePosition, true)
}

const hideTooltip = () => {
    clearShowTimer()
    isVisible.value = false
    removeAriaDescription()
    stopPositionTracking()
}

const syncVisibility = () => {
    if (shouldBeVisible.value) showTooltip()
    else hideTooltip()
}

const onMouseEnter = () => {
    isPointerInside.value = true
    syncVisibility()
}

const onMouseLeave = () => {
    isPointerInside.value = false
    syncVisibility()
}

const onFocusIn = () => {
    isFocusInside.value = true
    syncVisibility()
}

const onFocusOut = () => {
    requestAnimationFrame(() => {
        isFocusInside.value = Boolean(triggerElement?.contains(document.activeElement))
        syncVisibility()
    })
}

const updatePosition = () => {
    if (isVisible.value) positionTooltip()
}

const animateTooltip = (element: Element, keyframes: Keyframe[], done: () => void) => {
    if (!(element instanceof HTMLElement)) {
        done()
        return
    }

    const animation = element.animate(keyframes, { duration: 200, easing: 'ease' })
    animation.addEventListener('finish', done, { once: true })
    animation.addEventListener('cancel', done, { once: true })
}

const enterTooltip = (element: Element, done: () => void) => {
    animateTooltip(element, [
        { opacity: 0, transform: 'scale(0.96)' },
        { opacity: 1, transform: 'scale(1)' }
    ], done)
}

const leaveTooltip = (element: Element, done: () => void) => {
    animateTooltip(element, [
        { opacity: 1, transform: 'scale(1)' },
        { opacity: 0, transform: 'scale(0.96)' }
    ], done)
}

onMounted(() => {
    const slotTrigger = anchorRef.value?.firstElementChild
    triggerElement = slotTrigger instanceof HTMLElement
        ? slotTrigger
        : anchorRef.value?.parentElement || null

    triggerElement?.addEventListener('mouseenter', onMouseEnter)
    triggerElement?.addEventListener('mouseleave', onMouseLeave)
    triggerElement?.addEventListener('focusin', onFocusIn)
    triggerElement?.addEventListener('focusout', onFocusOut)
})

onBeforeUnmount(() => {
    hideTooltip()
    triggerElement?.removeEventListener('mouseenter', onMouseEnter)
    triggerElement?.removeEventListener('mouseleave', onMouseLeave)
    triggerElement?.removeEventListener('focusin', onFocusIn)
    triggerElement?.removeEventListener('focusout', onFocusOut)
})
</script>

<style scoped>
.tooltip-anchor {
    display: contents;
}

.tooltip-content {
    position: fixed;
    z-index: 100;
    padding: 10px 16px;
    border: 1px solid rgb(255 255 255 / 0.58);
    border-radius: 14px;
    background: rgb(255 255 255 / 0.6);
    color: rgb(34 34 34);
    font-family: Manrope, sans-serif;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.35;
    pointer-events: none;
    white-space: nowrap;
    box-shadow: 0 8px 20px oklch(0% 0 0 / 0.1), 0 0 30px oklch(0% 0 0 / 0.06);
    -webkit-backdrop-filter: blur(6px);
    backdrop-filter: blur(6px);
}

.tooltip-content--wrap {
    max-width: min(320px, calc(100vw - 16px));
    white-space: pre-line;
}

</style>
